import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import toast from 'react-hot-toast';
import Toolbar from '../../editor/Toolbar.jsx';
import LeftSidebar from '../../editor/LeftSidebar.jsx';
import Canvas from '../../editor/Canvas.jsx';
import Inspector from '../../editor/Inspector.jsx';
import AiPromptModal from '../../editor/AiPromptModal.jsx';
import PreviewModal from '../../editor/PreviewModal.jsx';
import ThemeModal from '../../editor/ThemeModal.jsx';
import PageSettingsModal from '../../editor/PageSettingsModal.jsx';
import DragPreview from '../../editor/DragPreview.jsx';
import { CanvasThemeProvider } from '../../editor/ThemeContext.jsx';
import { useEditor } from '../../editor/editorStore.js';
import { newBlock, blockAccepts } from '../../editor/blockSchema.js';
import { pagesApi, sitesApi } from '../../api/sites.js';
import { apiError } from '../../api/client.js';

export default function EditorPage() {
  const { siteId, pageId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [showAi, setShowAi] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [pageMeta, setPageMeta] = useState(null);
  const [drag, setDrag] = useState(null); // { kind: 'palette'|'block', type }

  const loadPage = useEditor((s) => s.loadPage);
  const blocks = useEditor((s) => s.blocks);
  const dirty = useEditor((s) => s.dirty);
  const selectedId = useEditor((s) => s.selectedId);
  const select = useEditor((s) => s.select);
  const setSaving = useEditor((s) => s.setSaving);
  const setPublishing = useEditor((s) => s.setPublishing);
  const markSaved = useEditor((s) => s.markSaved);
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);
  const insertBlock = useEditor((s) => s.insertBlock);
  const moveBlockTo = useEditor((s) => s.moveBlockTo);
  const removeBlock = useEditor((s) => s.removeBlock);
  const duplicateBlock = useEditor((s) => s.duplicateBlock);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        const [s, p] = await Promise.all([sitesApi.show(siteId), pagesApi.show(pageId)]);
        if (cancelled) return;
        setSite(s);
        loadPage(p.page);
        setPageMeta(p.page);
      } catch (err) {
        toast.error(apiError(err));
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [siteId, pageId, loadPage]);

  // Keyboard shortcuts. Inline-editing fields take precedence — we ignore
  // shortcuts when focus is on an input, textarea, or contentEditable.
  useEffect(() => {
    function isEditingTarget(t) {
      if (!t) return false;
      const tag = t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (t.isContentEditable) return true;
      return false;
    }

    function onKey(e) {
      if (isEditingTarget(e.target)) {
        // Allow Esc inside editables to blur, but still let undo/redo work.
        if (e.key === 'Escape') e.target.blur?.();
        return;
      }

      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (meta && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }
      if (meta && e.key === 's') {
        e.preventDefault();
        save();
        return;
      }
      if (meta && e.key === 'd' && selectedId) {
        e.preventDefault();
        duplicateBlock(selectedId);
        return;
      }
      if (e.key === 'Escape') {
        select(null);
        return;
      }
      if (selectedId && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        removeBlock(selectedId);
        return;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    function onBeforeUnload(e) {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Auto-save: every 5s, if there are unsaved changes and we're not already
  // mid-save, persist them silently (no toast). The manual Save button still
  // works and shows a toast — this is a safety net, not the primary path.
  useEffect(() => {
    const id = setInterval(() => {
      const s = useEditor.getState();
      if (!s.dirty || s.saving || s.publishing) return;
      autoSave();
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  async function autoSave() {
    setSaving(true);
    try {
      const latest = useEditor.getState().blocks;
      await pagesApi.update(pageId, { draft_blocks: latest });
      markSaved();
    } catch {
      // Auto-save is silent on failure — user will retry on next tick or
      // hit explicit Save (which surfaces the error).
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      await pagesApi.update(pageId, { draft_blocks: blocks });
      markSaved();
      toast.success('Draft saved');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    setPublishing(true);
    try {
      await pagesApi.update(pageId, { draft_blocks: blocks });
      const page = await pagesApi.publish(pageId);
      markSaved();
      if (site && !site.is_published) {
        const updated = await sitesApi.update(site.id, { is_published: true });
        setSite(updated);
      }
      toast.success('Published');
      loadPage(page);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPublishing(false);
    }
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function resolveTarget(over) {
    if (!over) return null;
    const data = over.data?.current;
    if (data?.kind === 'empty') return { parentId: data.parentId, index: 0 };
    if (data?.kind === 'append') {
      const parentId = data.parentId;
      const len = parentId === '__root__'
        ? blocks.length
        : Math.max(0, findBlockChildCount(blocks, parentId));
      return { parentId, index: len };
    }
    if (data?.source === 'block') {
      return { parentId: data.parentId, index: data.index };
    }
    return null;
  }

  function onDragStart(e) {
    const data = e.active?.data?.current;
    if (data?.source === 'palette') {
      setDrag({ kind: 'palette', type: data.type, block: null });
    } else if (data?.source === 'block') {
      // Layer drags use 'layer-{id}' as active id; canvas drags use the
      // block id directly. Either way, data.blockId is the source block.
      const sourceId = data.blockId ?? e.active.id;
      const moving = findBlockById(blocks, sourceId);
      setDrag({ kind: 'block', type: data.type, block: moving });
    }
  }

  function onDragEnd(e) {
    setDrag(null);
    const { active, over } = e;
    if (!over) return;
    const target = resolveTarget(over);
    if (!target) return;

    const activeData = active.data?.current;

    if (activeData?.source === 'palette' && activeData.type) {
      if (!canPlace(activeData.type, target.parentId)) {
        toast.error(`Can't place ${activeData.type} there.`);
        return;
      }
      insertBlock(newBlock(activeData.type), target.parentId, target.index);
      return;
    }

    if (activeData?.source === 'block') {
      if (active.id === over.id) return;
      const sourceId = activeData.blockId ?? active.id;
      // Don't move onto self via the layer-row droppable (different id but
      // same block).
      if (over.data?.current?.blockId === sourceId) return;
      if (!canPlace(activeData.type, target.parentId)) {
        toast.error(`Can't drop ${activeData.type} there.`);
        return;
      }
      moveBlockTo(sourceId, target.parentId, target.index);
    }
  }

  function onDragCancel() {
    setDrag(null);
  }

  function canPlace(childType, parentId) {
    // `column` blocks are managed by their parent Columns block — never
    // allow them to live anywhere else (including root).
    if (childType === 'column') {
      if (parentId === '__root__') return false;
      const parent = findBlockType(blocks, parentId);
      return parent === 'columns';
    }
    if (parentId === '__root__') return true;
    const parent = findBlockType(blocks, parentId);
    if (!parent) return false;
    return blockAccepts(parent, childType);
  }

  return (
    <div className="grid h-screen grid-rows-[3.5rem,1fr] bg-ink-50 dark:bg-ink-950">
      <Toolbar
        siteName={site?.name}
        onBack={() => navigate('/sites')}
        onSave={save}
        onPublish={publish}
        onOpenAi={() => setShowAi(true)}
        onPreview={() => setShowPreview(true)}
        onOpenTheme={() => setShowTheme(true)}
        onOpenPageSettings={() => setShowPageSettings(true)}
      />
      <CanvasThemeProvider value={site?.theme}>
        <DndContext
          sensors={sensors}
          collisionDetection={blockAwareCollision}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <div className="grid grid-cols-[16rem,1fr,18rem] overflow-hidden">
            <LeftSidebar />
            <Canvas />
            <Inspector />
          </div>
          <DragOverlay dropAnimation={null}>
            {drag ? (
              <DragPreview kind={drag.kind} blockType={drag.type} block={drag.block} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </CanvasThemeProvider>

      <AiPromptModal open={showAi} onClose={() => setShowAi(false)} />
      <PreviewModal open={showPreview} onClose={() => setShowPreview(false)} site={site} />
      <ThemeModal
        open={showTheme}
        onClose={() => setShowTheme(false)}
        site={site}
        onSaved={(updated) => setSite(updated)}
      />
      <PageSettingsModal
        open={showPageSettings}
        onClose={() => setShowPageSettings(false)}
        page={pageMeta}
        onSaved={(updated) => setPageMeta(updated)}
      />
    </div>
  );
}

// Local helpers only — block lookup + ops live in editorStore.
function findBlockChildCount(blocks, id) {
  for (const b of blocks) {
    if (b.id === id) return b.children?.length ?? 0;
    if (b.children) {
      const inner = findBlockChildCount(b.children, id);
      if (inner !== -1) return inner;
    }
  }
  return -1;
}

function findBlockType(blocks, id) {
  for (const b of blocks) {
    if (b.id === id) return b.type;
    if (b.children) {
      const inner = findBlockType(b.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function findBlockById(blocks, id) {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (b.children) {
      const inner = findBlockById(b.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

// Custom collision detection that respects block-type semantics:
// - Cross-context is allowed: a layer-row drag can land on a canvas
//   block, and a canvas drag can land on a layer row. closestCenter
//   naturally picks whichever rect is nearest the cursor, so the user's
//   intent follows their pointer between the two surfaces.
// - Dragging a `column`: only sibling columns (same parent) are valid
//   targets — closestCenter would otherwise pick inner content in tall
//   columns. Sibling matching uses data.parentId, which is identical
//   across canvas and layer rows because layers mirror the tree.
// - Dragging non-column content: exclude `column` wrapper droppables so
//   inner droppables (the column body's sortables / append zones) win.
function blockAwareCollision(args) {
  const activeData = args.active?.data?.current;
  const isColumnDrag = activeData?.source === 'block' && activeData?.type === 'column';

  const filtered = args.droppableContainers.filter((c) => {
    const d = c.data?.current;

    if (isColumnDrag) {
      return d?.source === 'block'
        && d?.type === 'column'
        && d?.parentId === activeData.parentId;
    }
    if (d?.source === 'block' && d?.type === 'column') return false;
    return true;
  });

  return closestCenter({ ...args, droppableContainers: filtered });
}
